"use client";

import { useMemo, useState, useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import {
  Sparkles,
  Link as LinkIcon,
  Shield,
  Wallet,
  Hash,
  Tag,
  ChevronRight,
  ExternalLink,
  Droplets,
  Trash2,
  X,
} from "lucide-react";
import { PublicKey } from "@solana/web3.js";
import AutobotHeader from "@/components/layout/Header/CommonHeader";
import PageLayout from "@/components/layout/PageLayout";
import useWallet from "@/hooks/useWallet";
import { useAuthModal } from "@/hooks/useAuthModal";
import apiService from "@/utils/apiService";
import { isMissingAuthTokenError } from "@/utils/authUtils";
import { trackEvent } from "@/utils/useGtm";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/navigation";
import {
  markTokenCreated,
  selectTokenStrengthScore,
  selectStrengthCriteria,
  selectTokenData,
  selectStrengthLoading,
} from "@/redux/slices/tokenStrengthSlice";
import {
  selectTokenCreatorForm,
  selectTokenCreatorToggles,
  setTokenCreatorForm,
  setTokenCreatorToggles,
  clearTokenCreatorForm,
  getDefaultForm,
  getDefaultToggles,
} from "@/redux/slices/tokenCreatorFormSlice";
import { fetchTokenStrengthCriteria } from "@/services/tokenStrengthService";
import SectionCard from "./components/SectionCard";
import SocialLinksSection, {
  validateSocialLink,
} from "@/components/SocialLinksSection";
import FileInput from "@/components/FileInput";
import TokenStrengthBar from "./components/TokenStrengthBar";
import TokenSubmitSection from "./components/TokenSubmitSection";
import {
  VideoSection,
  HowToUseSection,
  FAQSection,
  FeaturesSection,
} from "./components/InfoSections";
import RelatedToolsSection from "@/components/seo/RelatedToolsSection";
import BaseSectionCard from "@/components/common/BaseSectionCard";
import Link from "next/link";
import {
  saveTokenCreatorImage,
  loadTokenCreatorImage,
  clearTokenCreatorImage,
} from "./utils/tokenCreatorImageDB";
import tokenService from "@/services/tokenService";
import ToggleSwitch from "./components/ToggleSwitch";
import { confirmTransactionWithStatusFallback } from "@/utils/walletTransactionHelpers";
import {
  BASE58_REGEX,
  LIQUIDITY_MAX,
  LOCKED_LIQUIDITY_MAX,
  MAX_DESCRIPTION,
  MAX_NAME,
  MAX_SYMBOL,
  MAX_TAGS,
  MIN_DESCRIPTION,
  PREDEFINED_TAGS,
  RAW_TX_SEND_OPTS,
  REVOKE_POINTS_EACH,
  SOCIAL_LINKS_POINTS,
  SPL_DECIMALS_MAX,
  SPL_DECIMALS_MIN,
  SPL_SUPPLY_MIN,
  TOKEN_CREATION_MAX,
  TOKEN_DETAIL_POINTS,
  TOKEN_IMAGE_COMPRESSION_MAX_SIZE_MB,
  TOKEN_IMAGE_COMPRESSION_MAX_WIDTH_OR_HEIGHT,
  TOKEN_CREATOR_TOGGLE_TRACK_CLASS,
  TOKEN_TYPE_SPL,
  VANITY_MAX_TOTAL_CHARS,
  baseFees,
} from "./constants";
import {
  countValidSocialLinks,
  getMaxSupplyForDecimals,
  isValidSolanaWalletAddress,
  numberToEnglish,
  sanitizeBase58,
  validateVanity,
} from "./utils/tokenCreatorHelpers";
import {
  buildMintOptionsFromUi,
  runSplMintTwoPhaseSigningFlow,
} from "./utils/splMintSigningFlow";
import { readInsufficientSolMessage } from "./utils/assertSufficientSol";
import { useToast } from "@/components/Toast/ToastContext";
import {
  getSeoContent,
  SEO_VARIANT_TOKEN_CREATOR,
} from "./seoContent";
import { getSplTokenCreatorRelatedLinks } from "./relatedToolLinks";

const StrengthDetailsModal = dynamic(
  () => import("./components/StrengthDetailsModal"),
  { ssr: false },
);
const TokenCreationSuccessModal = dynamic(
  () => import("./components/TokenCreationSuccessModal"),
  { ssr: false },
);

export default function SplTokenCreatorPage({
  seoVariant = SEO_VARIANT_TOKEN_CREATOR,
  formOnly = false,
}) {
  const seo = getSeoContent(seoVariant);
  const slugPrefix = seo.slugPrefix;
  const formUi = seo.form;
  const hideDecimalsUi = formUi.hideDecimalsUi;
  const {
    connect,
    isConnected,
    walletAddress,
    isConnecting,
    connection,
    signTransaction: walletSignTransaction,
  } = useWallet();
  const { requireAuth, openLoginModal } = useAuthModal();
  const { showError } = useToast();
  const dispatch = useDispatch();
  const router = useRouter();
  const strengthCriteria = useSelector(selectStrengthCriteria);
  const tokenData = useSelector(selectTokenData);
  const strengthLoading = useSelector(selectStrengthLoading);
  const savedForm = useSelector(selectTokenCreatorForm);
  const savedToggles = useSelector(selectTokenCreatorToggles);

  const getInitialForm = () => ({
    ...getDefaultForm(),
    image: null,
    dexLogo: null,
    dexBanner: null,
    feePercent: "",
    maxFee: "500",
    authorityWallet: "",
    withdrawAuthority: "",
  });

  const [form, setForm] = useState(getInitialForm);
  const [toggles, setToggles] = useState(getDefaultToggles);
  const [decimalsInput, setDecimalsInput] = useState("9"); // string for keyboard input; 0-9 only
  const tokenType = TOKEN_TYPE_SPL;

  const [showSocialLinksSection, setShowSocialLinksSection] = useState(false);
  const [showAdvancedOptionsSection, setShowAdvancedOptionsSection] =
    useState(false);
  const [showRevokeAuthoritiesSection, setShowRevokeAuthoritiesSection] =
    useState(false);

  const [isTokenImageCompressing, setIsTokenImageCompressing] = useState(false);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState(null);

  // Catch "Origin not allowed" from wallet so it doesn't become an uncaught rejection and reload/redirect
  useEffect(() => {
    const handler = (event) => {
      const msg = event.reason?.message ?? String(event.reason ?? "");
      if (msg.toLowerCase().includes("origin not allowed")) {
        event.preventDefault();
        event.stopPropagation();
        setValidationError(
          "Your wallet is blocking this site. Add this domain in your wallet's allowed list or try another browser.",
        );
      }
    };
    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, []);

  // Hydrate form and toggles from Redux on mount (client-only; avoids hydration mismatch)
  useEffect(() => {
    setForm((prev) => ({
      ...savedForm,
      image: prev.image,
      dexLogo: prev.dexLogo,
      dexBanner: prev.dexBanner,
    }));
    setToggles(savedToggles);
    setDecimalsInput(String(savedForm.decimals ?? 9));
    loadTokenCreatorImage().then((blob) => {
      if (blob) {
        const file = new File([blob], "token-image.png", {
          type: blob.type || "image/png",
        });
        setForm((prev) => ({ ...prev, image: file }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist form (serializable fields only) and toggles to Redux (skip first run to avoid overwriting after hydrate)
  const persistSkipFirstRef = useRef(true);
  useEffect(() => {
    if (persistSkipFirstRef.current) {
      persistSkipFirstRef.current = false;
      return;
    }
    const serializable = { ...form };
    delete serializable.image;
    delete serializable.dexLogo;
    delete serializable.dexBanner;
    dispatch(setTokenCreatorForm(serializable));
    dispatch(setTokenCreatorToggles(toggles));
  }, [form, toggles, dispatch]);

  // Crypto SEO pages: decimals stay 9; field hidden in UI only
  useEffect(() => {
    if (!hideDecimalsUi) return;
    setForm((prev) =>
      prev.decimals === 9 ? prev : { ...prev, decimals: 9 },
    );
    setDecimalsInput("9");
  }, [hideDecimalsUi, seoVariant]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Split error states to prevent contamination
  const [validationError, setValidationError] = useState("");
  const [mintError, setMintError] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState(null);

  // Job-based minting state
  const [mintJob, setMintJob] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [mintJobStatus, setMintJobStatus] = useState(null);
  const [isCancellingMint, setIsCancellingMint] = useState(false);

  // Polling: use backend's suggestedPollMs so we never send a new request before the server asks (e.g. vanity mining).
  const pollingTimeoutRef = useRef(null);
  const pollingActiveRef = useRef(false);
  const pollingConsecutiveFailuresRef = useRef(0);
  const POLL_INTERVAL_DEFAULT_MS = 10000; // used only when backend doesn't send suggestedPollMs
  const POLL_INTERVAL_MIN_MS = 5000; // minimum wait between requests
  const MAX_CONSECUTIVE_FAILURES = 5; // stop after 5 timeouts/no-response in a row

  // Token strength scoring state
  const [showStrengthDetails, setShowStrengthDetails] = useState(false);

  // Prevent duplicate mint execution
  const mintInProgressRef = useRef(false);

  // Prevent duplicate job execution
  const executedJobRef = useRef(new Set());

  const formattedSupply = useMemo(() => {
    const num = Number(form.supply || 0);
    if (Number.isNaN(num)) return "0";
    return num.toLocaleString("en-US");
  }, [form.supply]);

  const adjustedSupply = useMemo(() => {
    const num = Number(form.supply || 0);
    if (Number.isNaN(num)) return "0";
    // UI supply is already the human-readable amount
    // No division needed - display as-is
    return num.toLocaleString("en-US");
  }, [form.supply]);

  const totalFees = useMemo(() => {
    let total = baseFees.base;
    if (toggles.creatorInfo) total += baseFees.creator;
    if (toggles.vanityAddress) total += baseFees.vanity;
    if (toggles.multiWallet) total += baseFees.multiWallet;
    // if (toggles.dexTools) total += baseFees.dexTools; // Commented out for future DEXTools integration
    if (toggles.revokeFreeze) total += baseFees.revoke;
    if (toggles.revokeMint) total += baseFees.revoke;
    if (toggles.revokeUpdate) total += baseFees.revoke;
    return total;
  }, [toggles]);

  // Token creation score (0–80): token details (equal), social links (full if >= 2), revoke (equal). No creator/vanity/multi-wallet.
  const tokenCreationBreakdown = useMemo(() => {
    const details = [];
    let score = 0;

    const tokenDetailChecks = [
      {
        id: "tokenName",
        name: "Name",
        check: () =>
          form.name.trim().length >= 2 && form.name.trim().length <= 32,
        description: "2–32 characters",
      },
      {
        id: "tokenSymbol",
        name: "Symbol",
        check: () =>
          form.symbol.trim().length >= 2 && form.symbol.trim().length <= 8,
        description: "2–8 characters",
      },
      {
        id: "decimals",
        name: "Decimals",
        check: () => form.decimals >= 0 && form.decimals <= 9,
        description: "Valid (0–9)",
      },
      {
        id: "supply",
        name: "Supply",
        check: () => !isNaN(Number(form.supply)) && Number(form.supply) > 0,
        description: "Positive amount",
      },
      {
        id: "description",
        name: "Description",
        check: () => form.description.trim().length >= 10,
        description: "At least 10 characters",
      },
      {
        id: "image",
        name: "Image",
        check: () => form.image !== null,
        description: "Token image uploaded",
      },
    ];
    tokenDetailChecks.forEach(({ id, name, check, description }) => {
      const achieved = check();
      if (achieved) score += TOKEN_DETAIL_POINTS;
      details.push({
        id,
        name,
        points: achieved ? TOKEN_DETAIL_POINTS : 0,
        achieved,
        description,
        maxPoints: TOKEN_DETAIL_POINTS,
      });
    });

    const socialCount = countValidSocialLinks(form);
    const socialPoints =
      socialCount >= 2
        ? SOCIAL_LINKS_POINTS
        : socialCount === 1
          ? Math.floor(SOCIAL_LINKS_POINTS / 2)
          : 0;
    score += socialPoints;
    details.push({
      id: "socialLinks",
      name: "Social Links",
      points: socialPoints,
      achieved: socialCount >= 2,
      description:
        socialCount >= 2
          ? `2+ valid links (you have ${socialCount})`
          : socialCount === 1
            ? `1 link = half score (you have 1)`
            : `2 links = full score, 1 link = half (you have 0)`,
      maxPoints: SOCIAL_LINKS_POINTS,
    });

    const revokeItems = [
      {
        id: "revokeFreeze",
        name: "Revoke Freeze",
        check: () => toggles.revokeFreeze,
        description: "Freeze authority revoked",
      },
      {
        id: "revokeMint",
        name: "Revoke Mint",
        check: () => toggles.revokeMint,
        description: "Mint authority revoked",
      },
      {
        id: "revokeUpdate",
        name: "Revoke Update",
        check: () => toggles.revokeUpdate,
        description: "Metadata update revoked",
      },
    ];
    revokeItems.forEach(({ id, name, check, description }) => {
      const achieved = check();
      if (achieved) score += REVOKE_POINTS_EACH;
      details.push({
        id,
        name,
        points: achieved ? REVOKE_POINTS_EACH : 0,
        achieved,
        description,
        maxPoints: REVOKE_POINTS_EACH,
      });
    });

    return { score: Math.min(score, TOKEN_CREATION_MAX), details };
  }, [form, toggles]);

  // Fetch token strength (liquidity) when we have a mint
  useEffect(() => {
    if (!tokenData?.mintAddress || !connection) return;
    fetchTokenStrengthCriteria(connection, tokenData.mintAddress, dispatch);
  }, [tokenData?.mintAddress, connection, dispatch]);

  // Token strength: 80% token creation (form), 10% liquidity, 10% locked liquidity
  const tokenStrength = useMemo(() => {
    const c =
      strengthCriteria &&
      strengthCriteria.mintAddress === tokenData?.mintAddress
        ? strengthCriteria
        : null;
    const tokenCreationScore = tokenCreationBreakdown.score;
    const liquidityPoolScore = c?.hasLiquidity ? LIQUIDITY_MAX : 0;
    const lockedLiquidityScore = c?.liquidityLocked ? LOCKED_LIQUIDITY_MAX : 0;
    const score = Math.min(
      tokenCreationScore + liquidityPoolScore + lockedLiquidityScore,
      100,
    );

    let level = "Incomplete";
    let color = "#ef4444";
    if (score === 100) {
      level = "Complete";
      color = "#22c55e";
    } else if (score >= 75) {
      level = "Almost Complete";
      color = "#84cc16";
    } else if (score >= 50) {
      level = tokenData?.mintAddress ? "Token Created" : "Good Progress";
      color = "#eab308";
    } else if (score >= 25) {
      level = "In Progress";
      color = "#f97316";
    } else if (strengthLoading && tokenData?.mintAddress) level = "Loading…";

    const details = [
      ...tokenCreationBreakdown.details,
      {
        id: "liquidityPool",
        name: "Liquidity Added",
        points: liquidityPoolScore,
        achieved: !!c?.hasLiquidity,
        description: "Add liquidity for full points",
        maxPoints: LIQUIDITY_MAX,
      },
      {
        id: "liquidityLocked",
        name: "Liquidity Locked",
        points: lockedLiquidityScore,
        achieved: !!c?.liquidityLocked,
        description: "LP mint authority renounced or held by known locker",
        maxPoints: LOCKED_LIQUIDITY_MAX,
      },
    ];

    return {
      score,
      level,
      color,
      details,
      maxScore: 100,
      tokenCreationScore,
      liquidityPoolScore,
      lockedLiquidityScore,
    };
  }, [
    tokenCreationBreakdown,
    tokenData?.mintAddress,
    strengthCriteria,
    strengthLoading,
  ]);

  const updateField = (key, value) => {
    if (key === "image") {
      if (value instanceof File) saveTokenCreatorImage(value);
      else if (value == null) clearTokenCreatorImage();
    }
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleUpload = async (file) => {
    if (!file) return null;

    const compressedBlob = await (
      await import("browser-image-compression")
    ).default(file, {
      maxSizeMB: TOKEN_IMAGE_COMPRESSION_MAX_SIZE_MB,
      maxWidthOrHeight: TOKEN_IMAGE_COMPRESSION_MAX_WIDTH_OR_HEIGHT,
      useWebWorker: true,
    });

    // `browser-image-compression` returns a Blob; convert back to a File
    // so `FileInput` preview + `saveTokenCreatorImage` continue to work.
    if (compressedBlob instanceof File) return compressedBlob;

    return new File([compressedBlob], file.name || "token-image.png", {
      type: compressedBlob?.type || file.type || "image/png",
    });
  };

  const handleTokenImageChange = async (file) => {
    if (file == null) {
      setIsTokenImageCompressing(false);
      updateField("image", null);
      return;
    }

    // Set file immediately so FileInput gets `value` and preview can render
    // while compression runs in the background.
    updateField("image", file);
    setIsTokenImageCompressing(true);
    try {
      const compressedFile = await handleUpload(file);
      if (compressedFile) updateField("image", compressedFile);
      else updateField("image", file);
    } catch (err) {
      console.warn(
        "Token image compression failed; using original image.",
        err,
      );
      updateField("image", file);
    } finally {
      setIsTokenImageCompressing(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (!isConnected) {
      await handleConnectWallet();
      return;
    }
    if (!requireAuth()) {
      setMintError("");
      return;
    }
    await handleSubmit();
  };

  const toggleFlag = (key) => {
    setToggles((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setVanityPrefixMode = (enable) => {
    if (enable) {
      setToggles((prev) => ({
        ...prev,
        vanityUsePrefix: true,
        vanityUseSuffix: false,
      }));
      updateField("suffix", "");
    } else {
      setToggles((prev) => ({ ...prev, vanityUsePrefix: false }));
      updateField("prefix", "");
    }
  };

  const setVanitySuffixMode = (enable) => {
    if (enable) {
      setToggles((prev) => ({
        ...prev,
        vanityUseSuffix: true,
        vanityUsePrefix: false,
      }));
      updateField("prefix", "");
    } else {
      setToggles((prev) => ({ ...prev, vanityUseSuffix: false }));
      updateField("suffix", "");
    }
  };

  const addTag = () => {
    const value = form.tagInput.trim().toLowerCase();
    if (!value || form.tags.includes(value) || form.tags.length >= MAX_TAGS)
      return;
    updateField("tags", [...form.tags, value]);
    updateField("tagInput", "");
  };

  const removeTag = (tag) => {
    updateField(
      "tags",
      form.tags.filter((t) => t !== tag),
    );
  };

  const selectPredefinedTag = (tag) => {
    const value = tag.toLowerCase();
    if (!form.tags.includes(value) && form.tags.length < MAX_TAGS) {
      updateField("tags", [...form.tags, value]);
    }
  };

  const totalDistributionPercent = useMemo(() => {
    return form.distribution.reduce((sum, row) => {
      const p = parseFloat(row.amount);
      return sum + (Number.isFinite(p) ? p : 0);
    }, 0);
  }, [form.distribution]);

  const remainingDistributionPercent = useMemo(
    () => Math.max(0, 100 - totalDistributionPercent),
    [totalDistributionPercent],
  );

  const addDistribution = () => {
    const walletRaw = form.distributionWallet.trim();
    const shareRaw = form.distributionShare.trim();
    if (!walletRaw || !shareRaw) return;
    if (form.distribution.length >= 10) return;

    if (!isValidSolanaWalletAddress(walletRaw)) {
      setValidationError(
        "Distribution wallet: invalid Solana address (32–44 Base58 characters).",
      );
      return;
    }
    try {
      new PublicKey(walletRaw);
    } catch {
      setValidationError("Distribution wallet: invalid Solana address.");
      return;
    }

    const shareNum = parseFloat(shareRaw);
    if (!Number.isFinite(shareNum) || shareNum <= 0 || shareNum > 100) {
      setValidationError(
        "Distribution share must be a number between 0 and 100.",
      );
      return;
    }
    if (shareNum > remainingDistributionPercent) {
      setValidationError(
        `Share cannot exceed remaining ${remainingDistributionPercent.toFixed(1)}%. Total must equal 100%.`,
      );
      return;
    }
    if (totalDistributionPercent + shareNum > 100.01) {
      setValidationError("Total distribution cannot exceed 100%.");
      return;
    }

    setValidationError("");
    updateField("distribution", [
      ...form.distribution,
      { wallet: walletRaw, amount: shareRaw },
    ]);
    updateField("distributionWallet", "");
    updateField("distributionShare", "");
  };

  const removeDistribution = (index) => {
    updateField(
      "distribution",
      form.distribution.filter((_, i) => i !== index),
    );
  };

  const handleClearAllData = () => {
    dispatch(clearTokenCreatorForm());
    setForm(getInitialForm());
    setToggles(getDefaultToggles());
    setDecimalsInput("9");
    setShowSocialLinksSection(false);
    setShowAdvancedOptionsSection(false);
    setShowRevokeAuthoritiesSection(false);
    setIsTokenImageCompressing(false);
    setValidationError("");
    setMintError("");
    setSuccess("");
    clearTokenCreatorImage();
  };

  function hasNonZeroSignature(sig) {
    if (!sig || sig.length === 0) return false;
    return sig.some((b) => b !== 0);
  }

  function getSignerIndex(transaction, signerPubkey) {
    const keys = transaction?.message?.staticAccountKeys || [];
    return keys.findIndex((k) => k.equals(signerPubkey));
  }

  function parsePublicKey(value, label) {
    const trimmed = (value || "").trim();
    if (!trimmed) throw new Error(`${label} is required.`);
    try {
      return new PublicKey(trimmed);
    } catch {
      throw new Error(`${label} is not a valid Solana address.`);
    }
  }

  async function signAndValidate(signTransaction, transaction, label) {
    const signed = await signTransaction(transaction);
    if (!hasNonZeroSignature(signed.signatures?.[0])) {
      throw new Error(`Wallet signature missing for ${label} transaction.`);
    }
    return signed;
  }

  async function simulateOrThrow(connectionRef, transaction, label) {
    const { value } = await connectionRef.simulateTransaction(transaction, {
      commitment: "confirmed",
      sigVerify: true,
    });
    if (value.err) {
      const logs = value.logs?.join("\n") ?? "No logs";
      throw new Error(
        `${label} simulation failed: ${JSON.stringify(value.err)}\n\nLogs:\n${logs}`,
      );
    }
  }

  async function buildMetadata({ includeExtendedFields = false }) {
    const decimals = Number(form.decimals || 0);

    const compressedImageFile = await handleUpload(form.image);
    if (!compressedImageFile) throw new Error("Image compression failed.");
    updateField("image", compressedImageFile);

    const imageRes = await apiService.uploadTokenImage(compressedImageFile);
    const imageUri = imageRes?.data?.imageUri;
    if (!imageUri)
      throw new Error("Image upload failed. No imageUri returned.");

    const metadataPayload = {
      name: form.name.trim(),
      symbol: form.symbol.trim(),
      supply: form.supply,
      decimals,
      imageUri,
      description: form.description.trim(),
      ...(includeExtendedFields && {
        website: form.website.trim(),
        telegram: form.telegram.trim(),
        discord: form.discord.trim(),
        twitter: form.twitter.trim(),
        github: form.github.trim(),
        reddit: form.reddit.trim(),
        medium: form.medium.trim(),
        instagram: form.instagram.trim(),
        youtube: form.youtube.trim(),
        linkedin: form.linkedin.trim(),
        tags: form.tags,
        ...(toggles?.creatorInfo && {
          creatorName: form.creatorName,
          creatorWebsite: form.creatorWebsite,
        }),
      }),
    };

    const metadataRes = await apiService.uploadMetadata(metadataPayload);
    const metadataUriValue = metadataRes?.data?.metadataUri;
    if (!metadataUriValue)
      throw new Error("Metadata upload failed. No metadataUri returned.");

    updateField("metadataUri", metadataUriValue);
    if (typeof window !== "undefined") {
      localStorage.setItem("temp_metadata_uri", metadataUriValue);
    }

    return { metadataUriValue, decimals };
  }

  const buildSharedMetadata = () =>
    buildMetadata({ includeExtendedFields: true });

  const handleSubmit = async () => {
    if (!isConnected) {
      setValidationError("Connect your wallet before creating the token.");
      return;
    }

    if (!form.name.trim() || !form.symbol.trim()) {
      setValidationError("Name and symbol are required.");
      return;
    }

    // Validate decimals
    const decimals = Number(form.decimals);
    if (isNaN(decimals) || decimals < 0 || decimals > 9) {
      setValidationError("Decimals must be a number between 0 and 9.");
      return;
    }

    // Validate supply (SPL: min 1, max depends on decimals)
    const supplyStr = (form.supply || "").replace(/\D/g, "") || "0";
    let supplyBig;
    try {
      supplyBig = BigInt(supplyStr);
    } catch {
      setValidationError("Supply must be a valid whole number.");
      return;
    }
    if (supplyBig < SPL_SUPPLY_MIN) {
      setValidationError(`Supply must be at least ${SPL_SUPPLY_MIN}.`);
      return;
    }
    const maxSupply = getMaxSupplyForDecimals(decimals);
    if (supplyBig > maxSupply) {
      setValidationError(
        `Supply exceeds SPL maximum for ${decimals} decimals (max ${maxSupply.toString()}).`,
      );
      return;
    }

    if ((form.description || "").trim().length < MIN_DESCRIPTION) {
      setValidationError(
        `Description must be at least ${MIN_DESCRIPTION} characters.`,
      );
      return;
    }

    const socialKeys = [
      "website",
      "telegram",
      "discord",
      "twitter",
      "github",
      "reddit",
      "medium",
      "instagram",
      "youtube",
      "linkedin",
    ];
    for (const key of socialKeys) {
      const err = validateSocialLink(form[key], key);
      if (err) {
        const label = key.charAt(0).toUpperCase() + key.slice(1);
        setValidationError(`${label}: ${err}`);
        return;
      }
    }

    if (!form.image) {
      setValidationError("Please upload a token image.");
      return;
    }

    if (toggles.vanityAddress) {
      // Strict validation before sending request
      try {
        validateVanity(form.prefix, form.suffix);
      } catch (validationError) {
        // Show specific validation error
        setValidationError(`Vanity address error: ${validationError.message}`);
        return;
      }
    } else {
      // Clear prefix/suffix when vanity is disabled
      updateField("prefix", "");
      updateField("suffix", "");
    }

    // Multi-wallet distribution validation
    if (toggles.multiWallet) {
      if (!form.distribution || form.distribution.length === 0) {
        setValidationError(
          "Multi-wallet distribution is enabled but no wallets are configured.",
        );
        return;
      }

      let totalPercentage = 0;
      for (let i = 0; i < form.distribution.length; i++) {
        const dist = form.distribution[i];

        // Validate wallet address
        try {
          new PublicKey(dist.wallet);
        } catch (error) {
          setValidationError(
            `Distribution wallet ${i + 1}: Invalid wallet address - ${error.message}`,
          );
          return;
        }

        // Validate percentage
        const percentage = parseFloat(dist.amount);
        if (isNaN(percentage) || percentage <= 0 || percentage > 100) {
          setValidationError(
            `Distribution wallet ${i + 1}: Percentage must be a number between 0 and 100.`,
          );
          return;
        }

        totalPercentage += percentage;
      }

      // Check if percentages sum to 100%
      if (Math.abs(totalPercentage - 100) > 0.01) {
        // Allow small floating point errors
        setValidationError(
          `Distribution percentages must sum to exactly 100%. Current sum: ${totalPercentage.toFixed(2)}%`,
        );
        return;
      }
    }

    // Clear ALL errors before starting
    setMintError("");
    setValidationError("");
    setError(null);
    setIsSubmitting(true);
    setSuccess("");

    try {
      const insufficientSol = await readInsufficientSolMessage({
        connection,
        walletAddress,
        requiredSol: totalFees,
      });
      if (insufficientSol) {
        setMintError(insufficientSol);
        showError(insufficientSol);
        setIsSubmitting(false);
        return;
      }

      const { metadataUriValue, decimals } = await buildSharedMetadata();

      const jobPayload = {
        payerPublicKey: walletAddress,
        tokenName: form.name.trim(),
        tokenSymbol: form.symbol.trim(),
        decimals,
        initialSupply: form.supply, // UI supply - backend converts to raw
        metadataUri: metadataUriValue,
        options: {
          vanityPrefix:
            toggles.vanityAddress &&
            toggles.vanityUsePrefix &&
            form.prefix.trim().length > 0
              ? form.prefix.trim()
              : null,
          vanitySuffix:
            toggles.vanityAddress &&
            toggles.vanityUseSuffix &&
            form.suffix.trim().length > 0
              ? form.suffix.trim()
              : null,
          multiWallet: toggles.multiWallet,
          distribution: toggles.multiWallet ? form.distribution : null,
        },
      };

      const jobResponse = await apiService.requestMintJob(jobPayload);
      const jobData = jobResponse?.data;

      if (!jobData?.jobId) {
        throw new Error("Failed to create mint job. Please try again.");
      }

      // Clear any previous validation errors when job is successfully created
      setMintJobStatus(null); // Reset job status

      // Store job data and start polling
      setMintJob(jobData);
      startMintJobPolling(jobData.jobId);

      // Set success message for job creation
      setSuccess(
        `Mint job created! Finding vanity address... (Job ID: ${jobData.jobId})`,
      );
    } catch (err) {
      if (isMissingAuthTokenError(err)) {
        setMintError("");
        openLoginModal();
        setIsSubmitting(false);
        return;
      }
      const message =
        err?.response?.data?.message ||
        err?.data?.message ||
        err?.message ||
        "Token creation failed.";
      setMintError(message);
      setIsSubmitting(false); // Reset submitting state on error
    }
  };

  // Reset all error states
  const resetErrors = () => {
    setValidationError("");
    setMintError("");
    setError(null);
  };

  const handleConnectWallet = async () => {
    try {
      await connect("phantom");
      trackEvent("token_creator_wallet_connect");
    } catch (err) {
      const msg = err?.message || "";
      if (msg.toLowerCase().includes("origin not allowed")) {
        setValidationError(
          "Your wallet is blocking this site. Add this domain in your wallet's allowed list or try another browser.",
        );
        return;
      }
      setValidationError(msg || "Wallet connection failed");
    }
  };

  // Vanity address is generated on the backend only; mint keypair never leaves the server (partialSign there, then secret wiped).
  // No client-side address generation — frontend only sends prefix/suffix and receives public key after job is ready.

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
    };
  }, [pollingInterval]);

  // Clear validation errors when prefix/suffix changes
  // IMPORTANT: This prevents stale validation errors from persisting
  // after successful async operations (mint job completion)
  useEffect(() => {
    setValidationError("");
  }, [form.prefix, form.suffix]);

  // Clear errors and vanity mode when vanity is disabled
  useEffect(() => {
    if (!toggles.vanityAddress) {
      setValidationError("");
      updateField("prefix", "");
      updateField("suffix", "");
      setToggles((prev) => ({
        ...prev,
        vanityUsePrefix: false,
        vanityUseSuffix: false,
      }));
    }
  }, [toggles.vanityAddress]);

  // Sync decimals input display when form.decimals is set externally (e.g. reset)
  useEffect(() => {
    const d = form.decimals;
    setDecimalsInput(
      typeof d === "number" && !Number.isNaN(d) ? String(d) : "",
    );
  }, [form.decimals]);

  // Auto-clear success message after 10 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess("");
      }, 10000); // Clear after 10 seconds

      return () => clearTimeout(timer);
    }
  }, [success]);

  // Reset mint state before starting new job
  const resetMintState = () => {
    pollingActiveRef.current = false;
    setMintJob(null);
    setMintJobStatus(null);
    resetErrors();
    setIsPolling(false);
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    executedJobRef.current.clear();
    mintInProgressRef.current = false;
  };

  const stopMintJobPolling = () => {
    pollingActiveRef.current = false;
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    setIsPolling(false);
  };

  const handleCancelMint = async () => {
    const jobId = mintJob?.jobId;
    if (!jobId || !isPolling || isCancellingMint) return;
    if (!walletAddress) {
      setMintError("Connect your wallet before cancelling the mint.");
      return;
    }
    setIsCancellingMint(true);
    setMintError("");
    try {
      await apiService.cancelMintJob(jobId, walletAddress);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Could not cancel mint. Try again.";
      setMintError(msg);
      setIsCancellingMint(false);
      return;
    }
    stopMintJobPolling();
    setMintJob(null);
    setMintJobStatus(null);
    setValidationError("");
    setSuccess("Mint process cancelled.");
    setIsSubmitting(false);
    mintInProgressRef.current = false;
    setIsCancellingMint(false);
  };

  // Mint job polling: wait for backend's suggestedPollMs before each new request (e.g. "Mining vanity address…").
  // No new request is sent until the suggested wait has elapsed.
  const startMintJobPolling = (jobId) => {
    if (isPolling) return;

    setMintError("");
    setValidationError("");
    setIsPolling(true);
    setMintJobStatus("pending");
    pollingActiveRef.current = true;
    pollingConsecutiveFailuresRef.current = 0;

    const getNextPollDelayMs = (status) => {
      const suggested = status?.suggestedPollMs;
      if (typeof suggested === "number" && suggested >= 0) {
        return Math.max(POLL_INTERVAL_MIN_MS, suggested);
      }
      return POLL_INTERVAL_DEFAULT_MS;
    };
    const runOnePoll = async () => {
      if (!pollingActiveRef.current) return;

      try {
        const statusResponse = await apiService.getMintStatus(jobId);
        pollingConsecutiveFailuresRef.current = 0;

        const status = statusResponse?.data;
        // Never store mint secret key in frontend state — backend does partialSign server-side; key must never be in browser.
        const sanitized =
          status && status.mint && "secretKey" in status.mint
            ? { ...status, mint: { publicKey: status.mint.publicKey } }
            : status;
        setMintJobStatus(sanitized);

        if (status?.status === "ready") {
          stopMintJobPolling();
          // Pass jobId explicitly — ready payloads may omit jobId and break get-signed-tx URL + dedupe ref.
          await handleMintSuccess(status, jobId);
          return;
        }
        if (status?.status === "failed") {
          console.error(`❌ Job ${jobId} failed:`, status.error);
          stopMintJobPolling();
          handleMintFailure(status.error);
          return;
        }
        // Backend still processing (e.g. "Mining vanity address…"): wait suggestedPollMs before next request
        const nextDelay = getNextPollDelayMs(status);
        pollingTimeoutRef.current = setTimeout(runOnePoll, nextDelay);
        return;
      } catch (err) {
        console.warn("Polling status check error:", err);

        const isTimeoutOrNetwork =
          err.message?.includes("timeout") ||
          err.message?.includes("network") ||
          err.type === "NETWORK_ERROR";

        if (isTimeoutOrNetwork) {
          pollingConsecutiveFailuresRef.current =
            (pollingConsecutiveFailuresRef.current || 0) + 1;
          if (
            pollingConsecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES
          ) {
            console.warn("Polling stopped: too many consecutive timeouts");
            stopMintJobPolling();
            setMintError(
              "No response from server after several attempts. Please check your connection and try again.",
            );
            return;
          }
        } else if (err.message?.includes("Non-base58 character")) {
          // Transient, keep polling
        } else if (err.status >= 500) {
          stopMintJobPolling();
          setMintError(
            "Server error while checking mint status. Please try again.",
          );
          return;
        }
        // On error retry after default delay (no suggestedPollMs from failed request)
        pollingTimeoutRef.current = setTimeout(
          runOnePoll,
          POLL_INTERVAL_DEFAULT_MS,
        );
      }
    };

    runOnePoll();
  };

  const handleMintSuccess = async (status, jobIdFromPoll) => {
    const jobId = status?.jobId ?? jobIdFromPoll;
    if (!jobId) {
      throw new Error(
        "Cannot finalize mint: missing job id. The server should include jobId on the ready payload, or reconnect and try again.",
      );
    }

    // Job-level execution guard (uses real job id — never `undefined`, which previously broke signing)
    if (executedJobRef.current.has(jobId)) {
      console.warn("Mint already executed for job", jobId);
      return;
    }
    executedJobRef.current.add(jobId);

    let signature = null;
    let mintPublicKey = status?.mint?.publicKey || null;

    try {
      resetErrors();

      // Validate wallet connection
      if (!isConnected || !walletAddress) {
        throw new Error(
          "Wallet is not connected. Please connect your wallet first.",
        );
      }
      if (!walletSignTransaction) {
        throw new Error(
          "Your wallet is not exposing signTransaction. Reconnect your wallet or try Phantom / Solflare.",
        );
      }

      const insufficientSol = await readInsufficientSolMessage({
        connection,
        walletAddress,
        requiredSol: totalFees,
      });
      if (insufficientSol) {
        executedJobRef.current.delete(jobId);
        setMintError(insufficientSol);
        showError(insufficientSol);
        return;
      }

      // SPL flow: backend builds unsigned tx, wallet signs first, backend finalizes additional signer.
      const buildUnsignedPayload = {
        asyncMintJobId: jobId,
        payerPublicKey: walletAddress,
        tokenName: form.name.trim(),
        tokenSymbol: form.symbol.trim(),
        decimals: Number(form.decimals),
        initialSupply: form.supply,
        metadataUri:
          form.metadataUri ||
          (typeof window !== "undefined"
            ? localStorage.getItem("temp_metadata_uri")
            : null),
        options: buildMintOptionsFromUi({ toggles, form }),
      };

      const mintFlowResult = await runSplMintTwoPhaseSigningFlow({
        apiService,
        connection,
        walletAddress,
        walletSignTransaction,
        buildUnsignedPayload,
        requestedJobId: jobId,
        onSignSessionRetry: () => {
          setMintJobStatus((prev) => ({
            ...(prev && typeof prev === "object" ? prev : {}),
            message:
              "The previous wallet prompt expired. Approve the new one to create the token.",
          }));
        },
      });
      signature = mintFlowResult.signature;
      mintPublicKey = mintFlowResult.mintPublicKey || mintPublicKey;

      // 4. Confirm — use a fresh blockhash context for polling (tx already landed via sendRawTransaction)
      const {
        blockhash: confirmBlockhash,
        lastValidBlockHeight: confirmLastValid,
      } = await connection.getLatestBlockhash("confirmed");
      await confirmTransactionWithStatusFallback(
        connection,
        signature,
        {
          blockhash: confirmBlockhash,
          lastValidBlockHeight: confirmLastValid,
          commitment: "confirmed",
        }
      );

      if (!mintPublicKey) {
        throw new Error("Mint public key missing from backend response.");
      }

      const mintPubkey = new PublicKey(mintPublicKey);
      const mintAccountInfo = await connection.getAccountInfo(mintPubkey);

      if (!mintAccountInfo) {
        throw new Error("Mint account not found on-chain after transaction");
      }

      // ⚠️ REMOVED: Duplicate database save (already done by backend in processVanityMintJob)
      // The backend already saves the token creation record when the job completes

      // 6. Mark token as created in Redux and store token data
      dispatch(
        markTokenCreated({
          mintAddress: mintPublicKey,
          tokenName: form.name,
          tokenSymbol: form.symbol,
          decimals: form.decimals,
          supply: form.supply,
        }),
      );

      // 7. Clear persisted image and show success modal (no auto-redirect)
      clearTokenCreatorImage();
      const explorerUrl = `https://explorer.solana.com/tx/${signature}`;
      const mintExplorerUrl = `https://explorer.solana.com/address/${mintPublicKey}`;
      setSuccessModalData({
        mintAddress: mintPublicKey,
        tokenName: form.name,
        symbol: form.symbol,
        explorerUrl,
        mintExplorerUrl,
      });
      setShowSuccessModal(true);
      trackEvent("token_creator_token_created");
      setSuccess("");

      // 8. Reset form state to allow creating another token (only if user comes back)
      // Note: Form reset is delayed in case of redirect
      const resetTimeout = setTimeout(() => {
        // Reset form to initial state
        setForm({
          name: "",
          symbol: "",
          decimals: 9,
          supply: "1000000000",
          description: "",
          image: null,
          website: "",
          telegram: "",
          discord: "",
          twitter: "",
          github: "",
          reddit: "",
          medium: "",
          tags: [],
          tagInput: "",
          creatorName: "",
          creatorWebsite: "https://www.idxsolana.io/",
          prefix: "",
          suffix: "",
          distribution: [],
          distributionWallet: "",
          distributionShare: "",
          dexWebsite: "",
          dexTelegram: "",
          dexDiscord: "",
          dexTwitter: "",
          dexLogo: null,
          dexBanner: null,
        });

        // Reset toggles
        setToggles({
          creatorInfo: false,
          vanityAddress: false,
          vanityUsePrefix: false,
          vanityUseSuffix: false,
          multiWallet: false,
          dexTools: false,
          revokeFreeze: false,
          revokeMint: false,
          revokeUpdate: false,
        });
        // Clear timeout reference if component unmounts
      }, 5000); // Wait 5 seconds before resetting (after redirect delay)

      // 8. Clean up temporary storage
      if (typeof window !== "undefined") {
        localStorage.removeItem("temp_metadata_uri");
      }

      // 9. Reset mint state
      resetMintState();

      // Store timeout for cleanup on unmount (caller should use this if needed)
      return () => clearTimeout(resetTimeout);
    } catch (err) {
      console.error("Mint success handling failed:", err);
      executedJobRef.current.delete(jobId);

      // Check for specific error types
      const errMsg = err?.message?.toLowerCase() || "";
      if (
        errMsg.includes("user rejected") ||
        errMsg.includes("denied") ||
        errMsg.includes("cancel")
      ) {
        setMintError("Transaction cancelled by user.");
      } else if (
        errMsg.includes("job result not found") ||
        errMsg.includes("expired")
      ) {
        setMintError("Mint job expired. Please start again.");
      } else if (err.status === 409) {
        // Handle duplicate record gracefully - token was already saved by backend
        const explorerUrl = signature
          ? `https://explorer.solana.com/tx/${signature}`
          : "Pending...";
        const mintAddress =
          mintPublicKey || status?.mint?.publicKey || "Unknown";
        setSuccess(
          `🎉 Token Created Successfully!\n\n` +
            `✅ Mint Address: ${mintAddress}\n` +
            `✅ Transaction: ${explorerUrl}\n\n` +
            `Token "${form.name}" (${form.symbol}) has been created!`,
        );
        resetMintState();
      } else {
        const apiMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message;
        setMintError(
          apiMessage
            ? `Failed to create token: ${apiMessage}`
            : "Failed to create token on-chain.",
        );
      }

      // Reset job state on error
      resetMintState();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMintFailure = (error) => {
    const errorMessage =
      error?.message ||
      error ||
      "Vanity mint failed. Try shorter prefix/suffix.";
    console.error("Mint job failed:", errorMessage);
    setMintError(errorMessage);
    resetMintState();
  };

  // TokenStrengthBar and StrengthDetailsModal moved to separate components for clarity

  const modals = (
    <>
      <StrengthDetailsModal
        isOpen={showStrengthDetails}
        onClose={() => setShowStrengthDetails(false)}
        strength={tokenStrength}
      />
      <TokenCreationSuccessModal
        isOpen={showSuccessModal}
        successModalData={successModalData}
        onClose={() => {
          setShowSuccessModal(false);
          setSuccessModalData(null);
        }}
        onAddLiquidity={() => {
          router.push("/liquidity-pool-creator");
          setShowSuccessModal(false);
          setSuccessModalData(null);
        }}
      />
    </>
  );

  const pageBody = (
    <>
      {!formOnly ? (
        <>
          <div className="absolute top-[-10%] left-[-10%] w-[40vw] h-[40vw] max-w-[600px] max-h-[600px] bg-blue-700/20 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[40vw] h-[40vw] max-w-[600px] max-h-[600px] bg-cyan-700/20 rounded-full blur-[120px] pointer-events-none" />
        </>
      ) : null}
      <TokenStrengthBar strength={tokenStrength} />
      <div
        className={
          formOnly
            ? "space-y-10"
            : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 space-y-10 relative z-10"
        }
      >
        {!formOnly ? (
          <BaseSectionCard
            as="section"
            className="p-6 sm:p-8 lg:p-10"
            contentClassName="space-y-6"
            aria-labelledby={`${slugPrefix}-heading`}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h1
                id={`${slugPrefix}-heading`}
                className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight"
              >
                {seo.pageTitle}
              </h1>
            </div>
            {seo.introParagraphs.map((paragraph) => (
              <p
                key={paragraph.slice(0, 48)}
                className="text-gray-300 text-sm sm:text-base"
              >
                {paragraph}
              </p>
            ))}
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-gray-200 leading-relaxed">
              {seo.splCallout}
            </div>
          </BaseSectionCard>
        ) : null}

        <section
          className="grid gap-6 sm:gap-10 lg:gap-16"
          aria-labelledby={`${slugPrefix}-form-sections-heading`}
        >
          <h2 id={`${slugPrefix}-form-sections-heading`} className="sr-only">
            Token creator form sections
          </h2>

          <div className="space-y-10">
              <SectionCard
                step={1}
                title={formUi.sectionTitle}
                subtitle={formUi.sectionSubtitle}
                headerAction={
                  <button
                    type="button"
                    onClick={handleClearAllData}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-600 bg-gray-800/80 px-2.5 py-1.5 text-xs font-medium text-gray-200 hover:bg-gray-700 hover:border-gray-500 hover:text-white transition"
                    title="Clear all form data"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    Clear data
                  </button>
                }
              >
                <div className="grid grid-cols-1 gap-5 sm:gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-300">
                      <span className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-cyan-300" />
                        {formUi.nameLabel}
                        <span className="text-red-500 text-base">*</span>
                      </span>
                      <span className="text-xs text-gray-400">
                        {(form.name || "").length}/{MAX_NAME}
                      </span>
                    </label>
                    <input
                      value={form.name}
                      onChange={(e) =>
                        updateField("name", e.target.value.slice(0, MAX_NAME))
                      }
                      placeholder="Ex: Solana"
                      maxLength={MAX_NAME}
                      className="w-full rounded-xl border border-gray-700 bg-gray-800/70 px-3 py-2 sm:px-4 sm:py-3 text-white placeholder-gray-500 focus:border-cyan-400 focus:outline-none"
                    />
                    <p className="text-xs text-gray-500">
                      Max 32 characters in your name
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-300">
                      <span className="flex items-center gap-2">
                        <Hash className="h-4 w-4 text-cyan-300" />
                        {formUi.symbolLabel}
                        <span className="text-red-500 text-base">*</span>
                      </span>
                      <span className="text-xs text-gray-400">
                        {(form.symbol || "").length}/{MAX_SYMBOL}
                      </span>
                    </label>
                    <input
                      value={form.symbol}
                      onChange={(e) =>
                        updateField(
                          "symbol",
                          e.target.value.slice(0, MAX_SYMBOL).toUpperCase(),
                        )
                      }
                      placeholder="Ex: SOL"
                      maxLength={MAX_SYMBOL}
                      className="w-full rounded-xl border border-gray-700 bg-gray-800/70 px-3 py-2 sm:px-4 sm:py-3 text-white placeholder-gray-500 focus:border-cyan-400 focus:outline-none"
                    />
                    <p className="text-xs text-gray-500">
                      Max 8 characters in your symbol
                    </p>
                  </div>
                  {!hideDecimalsUi ? (
                    <div className="space-y-2">
                      <label className="text-sm text-gray-300">
                        Decimals
                        <span className="text-red-500 text-base ml-1">*</span>
                        <span className="text-xs text-gray-400 ml-1">
                          (SPL: {SPL_DECIMALS_MIN}–{SPL_DECIMALS_MAX})
                        </span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        maxLength={1}
                        value={decimalsInput}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const digitOnly = raw.replace(/[^0-9]/g, "");
                          const single = digitOnly.slice(-1);
                          setDecimalsInput(single);
                          if (single === "") {
                            return;
                          }
                          const num = parseInt(single, 10);
                          if (
                            num >= SPL_DECIMALS_MIN &&
                            num <= SPL_DECIMALS_MAX
                          ) {
                            updateField("decimals", num);
                          }
                        }}
                        onBlur={() => {
                          if (decimalsInput === "") {
                            updateField("decimals", 9);
                            setDecimalsInput("9");
                          }
                        }}
                        placeholder="9"
                        className="w-full rounded-xl border border-gray-700 bg-gray-800/70 px-3 py-2 sm:px-4 sm:py-3 text-white placeholder-gray-500 focus:border-cyan-400 focus:outline-none"
                      />
                      <p className="text-xs text-gray-500">
                        SPL tokens allow 0–9 decimals (9 recommended for 1 billion
                        supply)
                      </p>
                    </div>
                  ) : null}
                  <div className="space-y-2">
                    <label className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-300">
                      <span>
                        {formUi.supplyLabel}
                        <span className="text-red-500 text-base ml-1">*</span>
                        {!hideDecimalsUi ? (
                          <span className="text-xs text-gray-400 ml-1">
                            (SPL min {SPL_SUPPLY_MIN}, max by decimals)
                          </span>
                        ) : null}
                      </span>
                      {form.supply && form.supply !== "0" && (
                        <span className="text-xs font-medium text-cyan-300/90">
                          {numberToEnglish(form.supply)}
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.supply}
                      onChange={(e) => {
                        const value = e.target.value;
                        const cleanValue = value.replace(/[^0-9]/g, "");
                        const finalValue =
                          cleanValue.replace(/^0+(?!$)/, "") || "0";
                        updateField("supply", finalValue);
                      }}
                      placeholder="1000000000"
                      className="w-full rounded-xl border border-gray-700 bg-gray-800/70 px-3 py-2 sm:px-4 sm:py-3 text-white placeholder-gray-500 focus:border-cyan-400 focus:outline-none"
                    />
                    {(() => {
                      const supplyBig = BigInt(form.supply || "0");
                      const maxAllowed = getMaxSupplyForDecimals(form.decimals);
                      const valid =
                        supplyBig >= SPL_SUPPLY_MIN && supplyBig <= maxAllowed;
                      if (form.supply && form.supply !== "0" && !valid) {
                        return (
                          <p className="text-xs text-amber-400">
                            {supplyBig < SPL_SUPPLY_MIN
                              ? `Min supply is ${SPL_SUPPLY_MIN}.`
                              : `Max supply for ${form.decimals} decimals is ${maxAllowed.toString()}.`}
                          </p>
                        );
                      }
                      return (
                        <p className="text-xs text-gray-500">
                          SPL: 1 to max above (e.g. 1,000,000 or 1 billion)
                        </p>
                      );
                    })()}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-300">
                    <span className="flex items-center gap-2">
                      <LinkIcon className="h-4 w-4 text-cyan-300" />
                      Sample Description
                      <span className="text-red-500 text-base">*</span>
                      <span className="text-xs text-gray-400">
                        (min {MIN_DESCRIPTION} chars)
                      </span>
                    </span>
                    <span className="text-xs text-gray-400">
                      {(form.description || "").length}/{MAX_DESCRIPTION}
                    </span>
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) =>
                      updateField(
                        "description",
                        e.target.value.slice(0, MAX_DESCRIPTION),
                      )
                    }
                    rows={3}
                    placeholder="Ex: First community token on Solana.."
                    className="w-full rounded-xl border border-gray-700 bg-gray-800/70 px-3 py-2 sm:px-4 sm:py-3 text-white placeholder-gray-500 focus:border-cyan-400 focus:outline-none"
                  />
                  {(form.description || "").length > 0 &&
                    (form.description || "").length < MIN_DESCRIPTION && (
                      <p className="text-xs text-amber-400">
                        At least {MIN_DESCRIPTION} characters recommended for
                        metadata.
                      </p>
                    )}
                  {(form.description || "").length >= MIN_DESCRIPTION && (
                    <p className="text-xs text-gray-500">
                      Description is used in token metadata (max{" "}
                      {MAX_DESCRIPTION} chars).
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <FileInput
                    label="Image *"
                    helper=".png, .jpg, .gif 1000x1000 px (square image)"
                    accept=".png,.jpg,.jpeg,.gif"
                    value={form.image}
                    onChange={(file) => handleTokenImageChange(file)}
                    maxSizeMB={15}
                    compressing={isTokenImageCompressing}
                  />
                </div>
              </SectionCard>

              <div className="space-y-3">
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500">
                  Optional settings
                </p>
                <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
              <SectionCard
                step={2}
                title="Social Links & Tags"
                subtitle={
                  showSocialLinksSection
                    ? "Add links to your token metadata. SPL/Metaplex metadata supports these optional social links."
                    : undefined
                }
                collapsible
                isExpanded={showSocialLinksSection}
                onToggle={() =>
                  setShowSocialLinksSection((prev) => !prev)
                }
              >
                {showSocialLinksSection && (
                  <>
                <SocialLinksSection form={form} updateField={updateField} />

                <div className="space-y-2 mt-4">
                  <label className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-300">
                    <span className="flex items-center gap-2 min-w-0">
                      <Tag className="h-4 w-4 text-cyan-300 shrink-0" />
                      Tags (optional)
                    </span>
                    <span className="text-xs text-gray-400">
                      {form.tags.length}/{MAX_TAGS}
                    </span>
                  </label>
                  <p className="text-xs text-gray-400">
                    Select tags that are most associated with your project - max
                    3 tags
                  </p>
                  <input
                    type="text"
                    value={form.tagInput}
                    onChange={(e) => updateField("tagInput", e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    placeholder="Enter tag"
                    className="w-full rounded-xl border border-gray-700 bg-gray-800/70 px-3 py-2 sm:px-4 sm:py-3 text-white placeholder-gray-500 focus:border-cyan-400 focus:outline-none"
                  />
                  <div className="flex flex-wrap gap-2 mt-2">
                    {PREDEFINED_TAGS.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => selectPredefinedTag(tag)}
                        disabled={
                          form.tags.includes(tag.toLowerCase()) ||
                          form.tags.length >= MAX_TAGS
                        }
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                          form.tags.includes(tag.toLowerCase())
                            ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/50 cursor-not-allowed"
                            : "bg-gray-800/70 text-gray-300 border border-gray-700 hover:border-cyan-400/50 hover:bg-gray-800"
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                  {form.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {form.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-2 rounded-full bg-cyan-500/15 px-3 py-1 text-xs font-semibold text-cyan-200"
                        >
                          {tag}
                          <button
                            type="button"
                            className="text-gray-400 hover:text-white"
                            onClick={() => removeTag(tag)}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                  </>
                )}
              </SectionCard>

              <SectionCard
                optionalTag="Optional"
                title="Advanced Options"
                subtitle={
                  showAdvancedOptionsSection
                    ? "Configure metadata creators, vanity, and multi-wallet splits."
                    : undefined
                }
                collapsible
                isExpanded={showAdvancedOptionsSection}
                onToggle={() =>
                  setShowAdvancedOptionsSection((prev) => !prev)
                }
              >
                {showAdvancedOptionsSection && (
                <div className="space-y-4">
                  {/* Creator Information */}
                  <ToggleSwitch
                    checked={toggles.creatorInfo}
                    onChange={() => toggleFlag("creatorInfo")}
                    label="Modify Creator Information"
                    description="Change the information of the creator in the metadata, by default it is IdxAutoBot."
                    price="0.1"
                    icon={Sparkles}
                  />
                  {toggles.creatorInfo && (
                    <div className="grid gap-4 sm:gap-5 sm:grid-cols-2 mt-3">
                      <div className="space-y-2">
                        <label className="text-sm text-gray-300">
                          Creator Name
                        </label>
                        <input
                          value={form.creatorName}
                          onChange={(e) =>
                            updateField("creatorName", e.target.value)
                          }
                          placeholder="IDX"
                          className="w-full rounded-xl border border-gray-700 bg-gray-800/70 px-3 py-2 sm:px-4 sm:py-3 text-white placeholder-gray-500 focus:border-cyan-400 focus:outline-none"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-gray-300">
                          Creator Website
                        </label>
                        <input
                          value={form.creatorWebsite}
                          onChange={(e) =>
                            updateField("creatorWebsite", e.target.value)
                          }
                          placeholder="https://www.idxsolana.io/"
                          className="w-full rounded-xl border border-gray-700 bg-gray-800/70 px-3 py-2 sm:px-4 sm:py-3 text-white placeholder-gray-500 focus:border-cyan-400 focus:outline-none"
                        />
                      </div>
                    </div>
                  )}

                  {/* Vanity Address */}
                  <ToggleSwitch
                    checked={toggles.vanityAddress}
                    onChange={() => toggleFlag("vanityAddress")}
                    label="Custom Address Generator"
                    description={
                      <>
                        Customize the beginning or the end of your token
                        contract address{" "}
                        <span className="text-yellow-400">
                          (Max 4 chars — prefix or suffix)
                        </span>
                        . Using a vanity address may take longer to process.
                      </>
                    }
                    price="0.1"
                    icon={Hash}
                  />
                  {toggles.vanityAddress && (
                    <div className="space-y-3 mt-3">
                      <div className="grid gap-4 sm:gap-5 sm:grid-cols-2">
                        {/* Prefix column */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-semibold text-white">
                              Prefix (Max 4)*
                            </span>
                            <div className="flex flex-shrink-0 items-center gap-2.5 sm:gap-3">
                              <label className="relative inline-flex cursor-pointer items-center">
                                <input
                                  type="checkbox"
                                  checked={toggles.vanityUsePrefix}
                                  onChange={(e) =>
                                    setVanityPrefixMode(e.target.checked)
                                  }
                                  className="peer sr-only"
                                />
                                <div className={TOKEN_CREATOR_TOGGLE_TRACK_CLASS} />
                              </label>
                              <span className="text-sm text-gray-300">
                                Enable
                              </span>
                            </div>
                          </div>
                          <input
                            value={form.prefix}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/\s+/g, "");
                              const clean = sanitizeBase58(raw);
                              updateField(
                                "prefix",
                                clean.slice(0, VANITY_MAX_TOTAL_CHARS),
                              );
                            }}
                            placeholder="IDX..."
                            disabled={!toggles.vanityUsePrefix}
                            className={`w-full rounded-xl border px-4 py-3 text-white placeholder-gray-500 focus:outline-none transition ${
                              toggles.vanityUsePrefix
                                ? "border-gray-700 bg-gray-800/70 focus:border-cyan-400"
                                : "border-gray-700/50 bg-gray-800/40 text-gray-500 cursor-not-allowed"
                            }`}
                          />
                          <p className="text-xs text-gray-500">
                            Base58. Max {VANITY_MAX_TOTAL_CHARS} chars.
                          </p>
                        </div>
                        {/* Suffix column */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-semibold text-white">
                              Suffix (Max 4)*
                            </span>
                            <div className="flex flex-shrink-0 items-center gap-2.5 sm:gap-3">
                              <label className="relative inline-flex cursor-pointer items-center">
                                <input
                                  type="checkbox"
                                  checked={toggles.vanityUseSuffix}
                                  onChange={(e) =>
                                    setVanitySuffixMode(e.target.checked)
                                  }
                                  className="peer sr-only"
                                />
                                <div className={TOKEN_CREATOR_TOGGLE_TRACK_CLASS} />
                              </label>
                              <span className="text-sm text-gray-300">
                                Enable
                              </span>
                            </div>
                          </div>
                          <input
                            value={form.suffix}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/\s+/g, "");
                              const clean = sanitizeBase58(raw);
                              updateField(
                                "suffix",
                                clean.slice(0, VANITY_MAX_TOTAL_CHARS),
                              );
                            }}
                            placeholder="...IDX"
                            disabled={!toggles.vanityUseSuffix}
                            className={`w-full rounded-xl border px-4 py-3 text-white placeholder-gray-500 focus:outline-none transition ${
                              toggles.vanityUseSuffix
                                ? "border-gray-700 bg-gray-800/70 focus:border-cyan-400"
                                : "border-gray-700/50 bg-gray-800/40 text-gray-500 cursor-not-allowed"
                            }`}
                          />
                          <p className="text-xs text-gray-500">
                            Base58. Max {VANITY_MAX_TOTAL_CHARS} chars.
                          </p>
                        </div>
                      </div>
                      <div className="rounded-xl border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
                        <strong>Processing time!</strong> In some cases,
                        generating a 4-character address may take up to 1 hour.
                      </div>
                    </div>
                  )}

                  {/* Multi-Wallet Distribution */}
                  <ToggleSwitch
                    checked={toggles.multiWallet}
                    onChange={() => toggleFlag("multiWallet")}
                    label="Multi-Wallet Supply Distribution"
                    description={
                      <>
                        Distribute the supply of your token on different wallets
                        within creation{" "}
                        <span className="text-yellow-400">
                          (max 10 wallets)
                        </span>
                        .
                      </>
                    }
                    price="0.1"
                    icon={Wallet}
                  />
                  {toggles.multiWallet && (
                    <div className="space-y-3 mt-3">
                      <div className="grid gap-4 sm:gap-5 sm:grid-cols-2">
                        <div className="space-y-1">
                          <input
                            value={form.distributionWallet}
                            onChange={(e) =>
                              updateField("distributionWallet", e.target.value)
                            }
                            placeholder="Wallet address (32–44 chars)"
                            className={`w-full rounded-xl border px-4 py-3 text-white placeholder-gray-500 focus:outline-none ${
                              form.distributionWallet.trim() &&
                              !isValidSolanaWalletAddress(
                                form.distributionWallet,
                              )
                                ? "border-red-500/50 bg-gray-800/70 focus:border-red-400"
                                : "border-gray-700 bg-gray-800/70 focus:border-cyan-400"
                            }`}
                          />
                          {form.distributionWallet.trim() &&
                            !isValidSolanaWalletAddress(
                              form.distributionWallet,
                            ) && (
                              <p className="text-xs text-red-400">
                                Invalid Solana address (32–44 Base58 characters)
                              </p>
                            )}
                        </div>
                        <div className="space-y-1">
                          <input
                            value={form.distributionShare}
                            onChange={(e) =>
                              updateField(
                                "distributionShare",
                                e.target.value.replace(/[^0-9.]/g, ""),
                              )
                            }
                            placeholder="Share % (e.g. 10)"
                            className={`w-full rounded-xl border px-4 py-3 text-white placeholder-gray-500 focus:outline-none ${(() => {
                              const v = form.distributionShare.trim();
                              if (!v)
                                return "border-gray-700 bg-gray-800/70 focus:border-cyan-400";
                              const n = parseFloat(v);
                              if (!Number.isFinite(n) || n <= 0 || n > 100)
                                return "border-red-500/50 bg-gray-800/70 focus:border-red-400";
                              if (n > remainingDistributionPercent)
                                return "border-amber-500/50 bg-gray-800/70 focus:border-amber-400";
                              return "border-gray-700 bg-gray-800/70 focus:border-cyan-400";
                            })()}`}
                          />
                          {form.distributionShare.trim() &&
                            (() => {
                              const n = parseFloat(form.distributionShare);
                              if (!Number.isFinite(n) || n <= 0 || n > 100)
                                return (
                                  <p className="text-xs text-red-400">
                                    Enter a number between 0 and 100
                                  </p>
                                );
                              if (n > remainingDistributionPercent)
                                return (
                                  <p className="text-xs text-amber-400">
                                    Max{" "}
                                    {remainingDistributionPercent.toFixed(1)}%
                                    remaining (total must be 100%)
                                  </p>
                                );
                              return null;
                            })()}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-xs text-gray-400">
                          Total:{" "}
                          <strong className="text-white">
                            {totalDistributionPercent.toFixed(1)}%
                          </strong>
                        </span>
                        <span className="text-xs text-gray-400">
                          Remaining:{" "}
                          <strong
                            className={
                              remainingDistributionPercent <= 0
                                ? "text-amber-400"
                                : "text-cyan-300"
                            }
                          >
                            {remainingDistributionPercent.toFixed(1)}%
                          </strong>
                        </span>
                        {remainingDistributionPercent <= 0 &&
                          form.distribution.length > 0 && (
                            <span className="text-xs text-amber-400">
                              Total is 100%. Remove an entry to add another.
                            </span>
                          )}
                      </div>
                      <button
                        onClick={addDistribution}
                        className="inline-flex items-center gap-2 rounded-xl bg-cyan-600 px-4 py-2 text-white text-sm font-semibold hover:bg-cyan-500 transition disabled:opacity-50"
                        disabled={
                          !form.distributionWallet.trim() ||
                          !form.distributionShare.trim() ||
                          form.distribution.length >= 10 ||
                          remainingDistributionPercent <= 0 ||
                          !isValidSolanaWalletAddress(
                            form.distributionWallet,
                          ) ||
                          (() => {
                            const n = parseFloat(form.distributionShare);
                            return (
                              !Number.isFinite(n) ||
                              n <= 0 ||
                              n > 100 ||
                              n > remainingDistributionPercent
                            );
                          })()
                        }
                      >
                        <Rocket className="h-4 w-4" />
                        Add wallet
                      </button>
                      <div className="space-y-2">
                        {form.distribution.map((row, idx) => (
                          <div
                            key={`${row.wallet}-${idx}`}
                            className="flex items-center justify-between gap-2 rounded-xl border border-gray-800 bg-gray-900/60 px-3 py-2 text-sm text-gray-200"
                          >
                            <span
                              className="truncate min-w-0 flex-1"
                              title={row.wallet}
                            >
                              {row.wallet}
                            </span>
                            <span className="font-semibold text-white flex-shrink-0">
                              {row.amount}%
                            </span>
                            <span
                              role="button"
                              tabIndex={0}
                              className="flex-shrink-0 cursor-pointer text-gray-400 hover:text-red-400 px-1"
                              onClick={() => removeDistribution(idx)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  removeDistribution(idx);
                                }
                              }}
                              aria-label="Remove"
                            >
                              ×
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                )}
              </SectionCard>

              {/* Revoke options: same program logic as revoke-freeze and revoke-mint-authority pages (setAuthority / updateMetadata), applied in a single tx at mint time. Keys map to getSignedTransaction options: revokeFreeze→revokeFreezeAuthority, revokeMint→revokeMintAuthority, revokeUpdate→immutableMetadata. */}
              <SectionCard
                step={3}
                title="Revoke Authorities"
                subtitle={
                  showRevokeAuthoritiesSection
                    ? "Freeze, Mint, and Update authorities can be revoked to attract more investors (+0.1 SOL each)."
                    : undefined
                }
                collapsible
                isExpanded={showRevokeAuthoritiesSection}
                onToggle={() =>
                  setShowRevokeAuthoritiesSection((prev) => !prev)
                }
              >
                {showRevokeAuthoritiesSection && (
                <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {[
                    {
                      key: "revokeFreeze",
                      title: "Revoke Freeze",
                      helper: "No one can freeze holders anymore",
                      icon: Shield,
                    },
                    {
                      key: "revokeMint",
                      title: "Revoke Mint",
                      helper: "No one can mint additional supply",
                      icon: Shield,
                    },
                    {
                      key: "revokeUpdate",
                      title: "Revoke Update",
                      helper: "Metadata can no longer be modified",
                      icon: Shield,
                    },
                  ].map((item) => (
                    <div
                      key={item.key}
                      className="flex flex-col gap-3 rounded-2xl border border-gray-800 bg-gray-950/60 p-4 sm:p-5 hover:border-cyan-500/50 transition"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                        <div className="flex min-w-0 items-center gap-2.5 text-white pr-1">
                          <item.icon className="h-4 w-4 shrink-0 text-cyan-300" />
                          <span className="font-semibold text-sm sm:text-base">
                            {item.title}
                          </span>
                        </div>
                        <label className="relative inline-flex shrink-0 cursor-pointer items-center self-start sm:self-center">
                          <input
                            type="checkbox"
                            checked={toggles[item.key]}
                            onChange={() => toggleFlag(item.key)}
                            className="peer sr-only"
                          />
                          <div className={TOKEN_CREATOR_TOGGLE_TRACK_CLASS} />
                        </label>
                      </div>
                      <p className="text-xs text-gray-400">{item.helper}</p>
                      <p className="text-xs font-semibold text-cyan-300">
                        +0.1 SOL
                      </p>
                    </div>
                  ))}
                </div>
                )}
              </SectionCard>
                </div>
              </div>

              <TokenSubmitSection
                isConnected={isConnected}
                walletAddress={walletAddress}
                totalFeesText={`${totalFees.toFixed(1)} SOL`}
                validationError={validationError}
                mintError={mintError}
                success={success}
                isSubmitting={isSubmitting}
                isConnecting={isConnecting}
                isPolling={isPolling}
                onPrimaryAction={handlePrimaryAction}
                onCancelMint={handleCancelMint}
                isCancellingMint={isCancellingMint}
                vanityEnabled={toggles.vanityAddress}
                progress={
                  mintJobStatus?.progressPct ?? mintJobStatus?.progress ?? 0
                }
                durationMs={mintJobStatus?.durationMs}
                statusMessage={mintJobStatus?.message}
              />

              {/* Liquidity Pool Redirect — card matches SectionCard / BaseSectionCard pattern */}
              <BaseSectionCard
                as="div"
                decorativeGlow={false}
                className="cursor-pointer border border-cyan-500/30 bg-blue-800/10 hover:border-cyan-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20 group"
                contentClassName="space-y-3 sm:space-y-4"
                role="link"
                tabIndex={0}
                aria-label="Go to liquidity pool creator"
                onClick={() => router.push("/liquidity-pool-creator")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    router.push("/liquidity-pool-creator");
                  }
                }}
              >
                <h3 className="font-semibold text-base sm:text-lg text-white pr-2">
                  Next step: create a liquidity pool
                </h3>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3 min-w-0">
                    <Droplets className="h-5 w-5 text-cyan-400 shrink-0 mt-0.5" />
                    <div className="flex flex-col gap-1 min-w-0 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-2 sm:gap-y-1">
                      <span className="text-white text-sm sm:text-base">
                        Want to create a liquidity pool?
                      </span>
                      <span className="text-cyan-400 group-hover:text-cyan-300 font-semibold text-sm sm:text-base transition-colors inline-flex items-center gap-1 group-hover:gap-2">
                        Open pool creator tool
                        <ExternalLink className="h-4 w-4 shrink-0" />
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-cyan-400 shrink-0 self-end sm:self-center group-hover:translate-x-1 transition-transform" />
                </div>
              </BaseSectionCard>
            </div>
          </section>
        </div>

        {!formOnly ? (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8 relative z-10">
            <section className="" aria-labelledby={`${slugPrefix}-guide-heading`}>
              <h2 id={`${slugPrefix}-guide-heading`} className="sr-only">
                Token creator guide and tutorial
              </h2>
              <VideoSection variant={seoVariant} />
            </section>
            <HowToUseSection tokenType={tokenType} variant={seoVariant} />

            <section aria-labelledby={`${slugPrefix}-faq-heading`}>
              <h2 id={`${slugPrefix}-faq-heading`} className="sr-only">
                Token creator frequently asked questions
              </h2>
              <FAQSection tokenType={tokenType} variant={seoVariant} />
            </section>

            {seo.showFeatures ? (
              <section aria-labelledby={`${slugPrefix}-features-heading`}>
                <h2 id={`${slugPrefix}-features-heading`} className="sr-only">
                  Token creator features
                </h2>
                <FeaturesSection />
              </section>
            ) : null}

            <RelatedToolsSection links={getSplTokenCreatorRelatedLinks(seoVariant)} />
          </div>
        ) : null}
    </>
  );

  return formOnly ? (
    <>
      {pageBody}
      {modals}
    </>
  ) : (
    <PageLayout>
      <AutobotHeader />
      <main className="min-h-screen bg-idx-canvas relative overflow-hidden pb-20">
        {pageBody}
      </main>
      {modals}
    </PageLayout>
  );
}
